import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { useMutation } from '@ubisend/pulse-hooks';
import { AnimatePresence } from '@ubisend/framer-motion';
import {
  ActionMenu,
  Divider,
  Flex,
  useModal,
  useNotification
} from '@ubisend/pulse-components';
import { PermissionFilter } from '@ubisend/pulse-auth';

import {
  useBuilder,
  useAllowedBlocks,
  useCanvas
} from '../../../../hooks/index';
import { deleteStepTrigger } from '../../../../api/index';
import { Block, types } from '../../../Blocks/index';
import { Wrapper } from '../Transition/TransitionNode';
import EditTriggerNode from './EditTriggerNode';
import NodeButton from '../../NodeButton';

const TriggerNode = ({ node, handleEditBlock }) => {
  const [edit, setEdit] = useState(false);

  const { dispatch } = useBuilder();

  const blocks = useAllowedBlocks(node.blocks, types);

  const { showModal, hideModal } = useModal();

  const { showSuccess } = useNotification();

  const { setPanningEnabled } = useCanvas();

  const { mutate } = useMutation({
    mutationFn: deleteStepTrigger,
    onSuccess: () => {
      showSuccess('Successfully deleted trigger');
      dispatch({
        type: 'DELETE_TRIGGER',
        stepId: node.meta.stepId,
        triggerId: node.id
      });
      hideModal();
    }
  });

  const handleDelete = () => {
    showModal({
      header: 'Delete trigger',
      body: `Are you sure you want to delete this trigger?`,
      handleConfirm: () => {
        mutate({ stepId: node.meta.stepId, triggerId: node.id });
      }
    });
  };

  return (
    <Wrapper
      onMouseLeave={() => setPanningEnabled(true)}
      onScroll={() => setPanningEnabled(false)}>
      <AnimatePresence>
        {edit && (
          <EditTriggerNode handleClose={() => setEdit(false)} node={node} />
        )}
      </AnimatePresence>
      {blocks.length > 0 && (
        <Flex col fat="true" xScroll ySpaceSm>
          {blocks.map((block, key) => (
            <Block
              key={key}
              onClick={() => handleEditBlock(block)}
              {...block}
            />
          ))}
        </Flex>
      )}
      <Flex absolute style={{ top: '0.5rem', right: '0.5rem' }}>
        <ActionMenu
          buttonProps={{
            'aria-label': 'Toggle trigger menu',
            onMouseDown: event => event.stopPropagation(),
            onMouseUp: event => event.stopPropagation()
          }}>
          <PermissionFilter can="edit conversations">
            <>
              <NodeButton
                variant="inline"
                icon="pencilAlt"
                onClick={() => {
                  setEdit(true);
                }}>
                Edit trigger
              </NodeButton>
              <Divider mtNone mbNone />
            </>
          </PermissionFilter>
          <PermissionFilter can="delete conversations">
            <NodeButton
              variant="inline"
              icon="trash"
              colour="danger"
              onClick={handleDelete}>
              Delete trigger
            </NodeButton>
          </PermissionFilter>
        </ActionMenu>
      </Flex>
    </Wrapper>
  );
};

TriggerNode.propTypes = {
  node: PropTypes.shape({
    id: PropTypes.number.isRequired,
    blocks: PropTypes.arrayOf(PropTypes.object).isRequired,
    meta: PropTypes.shape({
      stepId: PropTypes.number.isRequired
    }).isRequired,
    base: PropTypes.object.isRequired
  }),
  handleEditBlock: PropTypes.func.isRequired
};

export default TriggerNode;
